import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./insights.module.scss";
import {
  blogPosts as allBlogPosts,
  type BlogPost,
} from "../../utils/data/blogPosts";
import { motion } from "framer-motion";
import { Search, TrendingUp, Bookmark, X } from "lucide-react";
import useGeoLocation from "../../components/geoLocation/GeoLoaction";
import {
  cmsPublicContent,
} from "../../utils/cmscontent/cms";
import { useLocation, useNavigate } from "react-router-dom";
import {
  cacheInsightPostMeta,
  extractInsightPreview,
  INSIGHT_POST_IDS,
  POST_CATEGORIES,
} from "../../utils/insights";
import { getBookmarkedIds } from "../../utils/bookmarks";
import { isKnownFallbackImageUrl, buildTitleImageUrl } from "../../utils/insightImage";
import PostCard from "../../components/PostCard/PostCard";
import { CACHE_VERSION } from "../../utils/version";
export type { BlogPost };
interface InsightPost {
  id: number;
  slug: string;
  title: string;
  description: string;
  imageUrl: string;
  category: string;
  trending: boolean;
  readingTime: number;
  views: number;
}

const CHUNK_SIZE = 3;
const INSIGHTS_PAGE_STORAGE_KEY = "insights_page";


// Cache version tied to the app build version — auto-busts on every deploy
const POST_CACHE_VERSION = CACHE_VERSION;
const POST_CACHE_SESSION_KEY = `insights_post_cache_${POST_CACHE_VERSION}`;

const postCache = new Map<number, InsightPost>();

try {
  const prefix = "insights_post_cache_";
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(prefix) && key !== POST_CACHE_SESSION_KEY) {
      sessionStorage.removeItem(key);
    }
  }
} catch {
  
}

try {
  const saved = sessionStorage.getItem(POST_CACHE_SESSION_KEY);
  if (saved) {
    const parsed: [number, InsightPost][] = JSON.parse(saved);
    parsed.forEach(([id, post]) => postCache.set(id, post));
  }
} catch {}

function persistPostCache() {
  try {
    const entries = Array.from(postCache.entries());
    if (entries.length > 200) {
      // Keep only frequently accessed: last 200
      entries.splice(0, entries.length - 200);
    }
    sessionStorage.setItem(POST_CACHE_SESSION_KEY, JSON.stringify(entries));
  } catch {
  }
}

const inFlightPostFetches = new Map<number, Promise<string[]>>();

function fetchCmsPostContent(
  postId: number,
  lat: string | number,
  lng: string | number,
): Promise<string[]> {
  // Keep request de-duplication scoped only to the insight post ID.
  const inFlight = inFlightPostFetches.get(postId);
  if (inFlight) return inFlight;

  const request = cmsPublicContent({
    post_id: String(postId),
    lat: String(lat),
    log: String(lng),
  }).catch(() => {
    inFlightPostFetches.delete(postId);
    return [];
  });

  inFlightPostFetches.set(postId, request);
  return request;
}

function extractImageCandidatesFromHtml(html: string): string[] {
  if (!html) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const candidates: string[] = [];

  const ogImage = doc
    .querySelector('meta[property="og:image"], meta[name="og:image"]')
    ?.getAttribute("content")
    ?.trim();
  if (ogImage) {
    candidates.push(new URL(ogImage, "https://public.ticca.com").toString());
  }

  const mediaEls = Array.from(doc.querySelectorAll("img, source"));
  mediaEls.forEach((el) => {
    const srcsetFirst = el
      .getAttribute("srcset")
      ?.split(",")?.[0]
      ?.trim()
      ?.split(" ")?.[0];

    const src =
      el.getAttribute("src")?.trim() ||
      srcsetFirst ||
      el.getAttribute("data-src")?.trim() ||
      el.getAttribute("data-lazy-src")?.trim() ||
      el.getAttribute("data-original")?.trim() ||
      "";

    if (!src) return;
    try {
      candidates.push(new URL(src, "https://public.ticca.com").toString());
    } catch {
    }
  });

  return Array.from(new Set(candidates.filter(Boolean)));
}

function extractTagsFromHtml(html: string): string[] {
  if (!html) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const tagsP = Array.from(doc.querySelectorAll("p")).find((p) =>
    p.querySelector("em")?.textContent?.trim().toLowerCase() === "tags:"
  );
  if (tagsP) {
    const em = tagsP.querySelector("em");
    if (em) em.remove();
    return tagsP.textContent
      ?.split(",")
      .map((t) => t.trim())
      .filter(Boolean) || [];
  }
  return [];
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border ">
      <div className="aspect-[16/10] bg-gray-200" />
      <div className="p-5 space-y-3">
        <div className="w-24 h-4 bg-gray-200 rounded" />
        <div className="h-5 bg-gray-200 rounded" />
        <div className="h-4 bg-gray-200 rounded" />
        <div className="flex gap-4 pt-4">
          <div className="w-16 h-4 bg-gray-200 rounded" />
          <div className="w-16 h-4 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );
}

function SearchLoader() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="flex flex-col items-center py-6">
      <div className="relative mb-5 h-12 w-12">
        <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
        <div className="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-blue-600" />
      </div>
      <p className="text-sm font-medium text-gray-600">
        Searching all posts...
      </p>
      <p className="mt-1 text-xs text-gray-400">
        Fetching content from the server — {elapsed}s elapsed
      </p>
    </div>
  );
}

function calcPerPage(w: number, h: number): number {
  // On mobile viewports, use a fixed batch so pagination isn't painful
  if (w < 768) return 8;
  const minCardW = 220;
  const minCardH = 260;
  const cols = Math.max(1, Math.floor(w / minCardW));
  const rows = Math.max(1, Math.floor(h / minCardH));
  return Math.min(cols * rows, cols * 4);
}

const Insights = () => {
  const geoLocation = useGeoLocation();
  const location = useLocation();
  const navigator = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    // Restore from navigation state first (coming back from BlogPost)
    try {
      const stateCat = (location.state as { selectedCategory?: string } | null)?.selectedCategory;
      if (stateCat) {
        // Sync to sessionStorage immediately so back-navigation picks it up
        sessionStorage.setItem("insights_selected_category", stateCat);
        return stateCat;
      }
    } catch {}
    // Then from sessionStorage
    try {
      const stored = sessionStorage.getItem("insights_selected_category");
      return stored || "All";
    } catch {
      return "All";
    }
  });
  const gridRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ w: typeof window !== 'undefined' ? window.innerWidth : 1400, h: typeof window !== 'undefined' ? window.innerHeight : 900 });
  const perPage = calcPerPage(containerSize.w, containerSize.h - 250); // subtract header + categories + footer

  const getInitialPage = () => {
    const pageFromState = Number(
      (location.state as { fromInsightsPage?: number } | null)?.fromInsightsPage,
    );
    if (Number.isFinite(pageFromState) && pageFromState >= 1) {
      return Math.floor(pageFromState);
    }

    const pageFromStorage = Number(sessionStorage.getItem(INSIGHTS_PAGE_STORAGE_KEY));
    if (Number.isFinite(pageFromStorage) && pageFromStorage >= 1) {
      return Math.floor(pageFromStorage);
    }

    return 1;
  };

  const [page, setPage] = useState<number>(() => getInitialPage());
  const [posts, setPosts] = useState<InsightPost[]>(() => {
    // If we have cached posts for page 1, show them immediately
    if (postCache.size > 0) {
      const firstPageIds = INSIGHT_POST_IDS.slice(0, perPage);
      const cached = firstPageIds.map((id) => postCache.get(id)).filter(Boolean) as InsightPost[];
      return cached;
    }
    return [];
  });
  const [loading, setLoading] = useState(() => postCache.size === 0);
  const [hasResolvedFirstFetch, setHasResolvedFirstFetch] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<number>>(() => getBookmarkedIds());

  const refreshBookmarks = () => setBookmarkedIds(getBookmarkedIds());
  const [goToInput, setGoToInput] = useState("");
  const [postCategoryMap, setPostCategoryMap] = useState<Record<number, string>>(() => {
    try {
      const stored = sessionStorage.getItem("insights_category_map_extras");
      const extras: Record<number, string> = stored ? JSON.parse(stored) : {};
      return { ...POST_CATEGORIES, ...extras };
    } catch {
      return POST_CATEGORIES;
    }
  });

  // Filtered post IDs based on selected category
  // Filtered post IDs based on selected category
  const filteredIds = selectedCategory === "All"
    ? INSIGHT_POST_IDS
    : selectedCategory === "Bookmarked"
    ? INSIGHT_POST_IDS.filter((id) => bookmarkedIds.has(id))
    : INSIGHT_POST_IDS.filter((id) => postCategoryMap[id] === selectedCategory);
  const filteredTotalPages = Math.ceil(filteredIds.length / perPage);
  // Scope visited-page tracking by category so switching filters doesn't show stale data
  const visitedStorageKey = `insights_visited_pages_${selectedCategory}`;
  const [visitedPages, setVisitedPages] = useState<Set<number>>(() => {
    try {
      const stored = sessionStorage.getItem(visitedStorageKey);
      return stored ? new Set(JSON.parse(stored)) : new Set<number>([1]);
    } catch {
      return new Set<number>([1]);
    }
  });

  // Re-initialise visited set when category changes
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(visitedStorageKey);
      setVisitedPages(stored ? new Set(JSON.parse(stored)) : new Set<number>([1]));
    } catch {
      setVisitedPages(new Set<number>([1]));
    }
  }, [selectedCategory]);

  const markPageVisited = (p: number) => {
    if (p < 1 || p > filteredTotalPages) return;
    setVisitedPages((prev) => {
      if (prev.has(p)) return prev;
      const next = new Set(prev);
      next.add(p);
      sessionStorage.setItem(visitedStorageKey, JSON.stringify([...next]));
      return next;
    });
  };

  const handleGoToPage = () => {
    const p = parseInt(goToInput, 10);
    if (p >= 1 && p <= filteredTotalPages && p !== page) {
      markPageVisited(p);
      setPage(p);
      setGoToInput("");
    }
  };
  const locationKey = geoLocation.loaded
    ? `${geoLocation.coordinates.lat}_${geoLocation.coordinates.lng}`
    : "0.0_0.0";

  function isTrendingFromId(id: number) {
    return id % 5 === 0; // always same result for same ID
  }

  function viewsFromId(id: number) {
    return id * 137; // deterministic, testable
  }

  // ResizeObserver for dynamic grid width (height uses viewport)
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const updateSize = () => {
      if (gridRef.current) {
        setContainerSize({ w: gridRef.current.offsetWidth, h: window.innerHeight });
      }
    };
    const ro = new ResizeObserver(() => updateSize());
    ro.observe(el);
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  // Helper: format raw CMS response into InsightPost
  function formatRawPost(res: string[], postId: number): InsightPost {
    const fullHtml = res.join(" ");
    const plainText = (() => {
      const div = document.createElement("div");
      div.innerHTML = fullHtml;
      return (div.textContent || div.innerText || "").trim();
    })();
    const words = plainText.split(/\s+/).filter(Boolean).length;
    const readingTime = Math.max(3, Math.ceil(words / 200));
    const firstPage = res[0] || "";
    const parsed = extractInsightPreview(firstPage);
    const extractedImageCandidates = extractImageCandidatesFromHtml(fullHtml);
    const titleUrl = buildTitleImageUrl(postId, parsed.title || "");
    // Discover category for unknown posts
    if (!(postId in POST_CATEGORIES) && fullHtml) {
      const tags = extractTagsFromHtml(fullHtml);
      if (tags.length > 0) {
        const discovered = tags[0];
        setPostCategoryMap((prev) => {
          if (prev[postId] === discovered) return prev;
          const next = { ...prev, [postId]: discovered };
          const extras: Record<number, string> = {};
          for (const key of Object.keys(next)) {
            const id = Number(key);
            if (!(id in POST_CATEGORIES)) extras[id] = next[id];
          }
          try { sessionStorage.setItem("insights_category_map_extras", JSON.stringify(extras)); } catch {}
          return next;
        });
      }
    }
    return {
      id: postId,
      slug: parsed.slug || `post-${postId}`,
      title: parsed.title || "Untitled",
      description: parsed.description || "No description available",
      imageUrl: (extractedImageCandidates[0] || titleUrl) || "",
      category: postCategoryMap[postId] || "General",
      trending: isTrendingFromId(postId),
      views: viewsFromId(postId),
      readingTime,
    };
  }

  useEffect(() => {
    sessionStorage.setItem(INSIGHTS_PAGE_STORAGE_KEY, String(page));
    markPageVisited(page);
  }, [page]);

  // Scroll to blog post grid when page changes
  useEffect(() => {
    if (gridRef.current) {
      const y = gridRef.current.getBoundingClientRect().top + window.scrollY - 20;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [page]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "TICCA | Insights ";
  }, []);

  // Save category to sessionStorage on change
  useEffect(() => {
    try {
      sessionStorage.setItem("insights_selected_category", selectedCategory);
    } catch {}
  }, [selectedCategory]);

  // Search: when user types, prefetch all uncached post titles for full results
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) return;

    // Debounce 200ms, fetch bigger batches for faster results
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      const missingIds = INSIGHT_POST_IDS.filter((id) => !postCache.has(id));
      if (missingIds.length === 0) {
        // Already all cached — update display from cache
        setSearching(false);
        const matched = Array.from(postCache.values()).filter((p) =>
          p.title.toLowerCase().includes(q),
        );
        if (matched.length > 0) setPosts(matched);
        return;
      }
      // Searching layer 2 — show loading animation
      setSearching(true);
      // Bigger batches = faster initial population
      for (let i = 0; i < missingIds.length; i += CHUNK_SIZE * 6) {
        const batch = missingIds.slice(i, i + CHUNK_SIZE * 6);
        const batchResults = await Promise.allSettled(
          batch.map((id) =>
            fetchCmsPostContent(id, geoLocation.coordinates.lat, geoLocation.coordinates.lng),
          ),
        );
        // Format and store each result in postCache so search can find them
        for (let j = 0; j < batchResults.length; j++) {
          const r = batchResults[j];
          if (r.status === "fulfilled" && r.value.length > 0) {
            const post = formatRawPost(r.value, batch[j]);
            postCache.set(batch[j], post);
            persistPostCache();
          }
        }
        // Trigger a re-render so search results update
        const allCached = Array.from(postCache.values());
        const q = searchQuery.toLowerCase().trim();
        const matched = allCached.filter((p) => p.title.toLowerCase().includes(q));
        if (matched.length > 0) {
          setPosts(matched);
        }
      }
      // All batches done — stop searching
      setSearching(false);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  // Reset to page 1 when category changes
  useEffect(() => {
    setPage(1);
  }, [selectedCategory]);

  // Handle "no posts to fetch" categories (e.g., empty Bookmarked) immediately
  useEffect(() => {
    if (filteredIds.length === 0) {
      setPosts([]);
      setLoading(false);
      setHasResolvedFirstFetch(true);
    }
  }, [filteredIds]);

  // Main fetch effect: post-level cache (survives resize / layout shifts)
  useEffect(() => {
    if (!locationKey) return;

    let cancelled = false;
    const idsForPage = filteredIds.slice(
      (page - 1) * perPage,
      page * perPage,
    );

    // Check how many posts we already have cached
    const cachedPosts = idsForPage.map((id) => postCache.get(id)).filter(Boolean) as InsightPost[];
    const missingIds = idsForPage.filter((id) => !postCache.has(id));

    // Stale-while-revalidate: silently check for fresh CMS content in background
    const revalidateCachedPage = async (ids: number[], isCancelled: () => boolean) => {
      const freshResults = await Promise.allSettled(
        ids.map((id) =>
          fetchCmsPostContent(id, geoLocation.coordinates.lat, geoLocation.coordinates.lng),
        ),
      );
      if (isCancelled()) return;
      let changed = false;
      for (let j = 0; j < freshResults.length; j++) {
        const result = freshResults[j];
        if (result.status === "fulfilled" && result.value.length > 0) {
          const freshPost = formatRawPost(result.value, ids[j]);
          const old = postCache.get(ids[j]);
          if (!old || old.title !== freshPost.title || old.description !== freshPost.description) {
            postCache.set(ids[j], freshPost);
            changed = true;
          }
        }
      }
      if (changed) {
        persistPostCache();
        const updated = ids.map((id) => postCache.get(id)).filter(Boolean) as InsightPost[];
        setPosts(updated);
      }
    };

    if (missingIds.length === 0 && cachedPosts.length > 0) {
      // All posts cached — render immediately, then revalidate silently
      setPosts(cachedPosts);
      setLoading(false);
      setHasResolvedFirstFetch(true);
      revalidateCachedPage(idsForPage, () => cancelled);
      return;
    }

    const fetchPage = async () => {
      if (cachedPosts.length === 0) setLoading(true);
      if (cachedPosts.length > 0) setPosts(cachedPosts);

      // Fetch missing posts progressively in chunks
      for (let i = 0; i < missingIds.length && !cancelled; i += CHUNK_SIZE) {
        const chunk = missingIds.slice(i, i + CHUNK_SIZE);
        const chunkResults = await Promise.allSettled(
          chunk.map((id) =>
            fetchCmsPostContent(id, geoLocation.coordinates.lat, geoLocation.coordinates.lng),
          ),
        );

        if (cancelled) return;

        for (let j = 0; j < chunkResults.length; j++) {
          const result = chunkResults[j];
          if (result.status === "fulfilled" && result.value.length > 0) {
            const post = formatRawPost(result.value, chunk[j]);
            postCache.set(chunk[j], post);          persistPostCache();          }
        }

        // Rebuild page from cache (includes freshly fetched + previously cached)
        const updated = idsForPage.map((id) => postCache.get(id)).filter(Boolean) as InsightPost[];
        if (updated.length > 0) {
          setPosts(updated);
        }
      }

      if (cancelled) return;

      cacheInsightPostMeta(
        idsForPage.map((id) => {
          const p = postCache.get(id);
          return p ? { id: p.id, title: p.title, slug: p.slug } : null;
        }).filter(Boolean) as { id: number; title: string; slug: string }[],
      );
      setLoading(false);
      setHasResolvedFirstFetch(true);
    };

    // Prefetch adjacent pages — just triggers fetchCmsPostContent which is deduplicated
    const prefetchAdjacent = async () => {
      const pagesToPrefetch: number[] = [];
      if (page > 1) pagesToPrefetch.push(page - 1);
      if (page < filteredTotalPages) pagesToPrefetch.push(page + 1);

      await Promise.all(
        pagesToPrefetch.map(async (p) => {
          const prefetchIds = filteredIds.slice(
            (p - 1) * perPage,
            p * perPage,
          );
          const needFetch = prefetchIds.filter((id) => !postCache.has(id));
          if (needFetch.length === 0 || cancelled) return;

          const results = await Promise.allSettled(
            needFetch.map((id) =>
              fetchCmsPostContent(id, geoLocation.coordinates.lat, geoLocation.coordinates.lng),
            ),
          );
          if (cancelled) return;
          for (let k = 0; k < results.length; k++) {
            const r = results[k];
            if (r.status === "fulfilled" && r.value.length > 0) {
              const post = formatRawPost(r.value, needFetch[k]);
              postCache.set(needFetch[k], post);
              persistPostCache();
            }
          }
        }),
      );
    };

    fetchPage().then(() => prefetchAdjacent());

    return () => {
      cancelled = true;
    };
  }, [page, locationKey, perPage, selectedCategory]);

  const blogPosts = allBlogPosts;

  // Build unique categories from loaded posts
  const categories = useMemo(() => {
    const cats = new Set<string>();
    cats.add("All");
    cats.add("Bookmarked");
    for (const id of INSIGHT_POST_IDS) {
      const cat = postCategoryMap[id];
      if (cat) cats.add(cat);
    }
    const sorted = Array.from(cats).filter((c) => c !== "All" && c !== "Bookmarked").sort();
    return ["All", "Bookmarked", ...sorted];
  }, [postCategoryMap]);

  // Calculate total stats
  const totalViews = blogPosts.reduce((sum, post) => sum + post.views, 0);
  const totalPosts = blogPosts.length;

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  const isSearching = searchQuery.trim().length > 0;

  // Build search results from ALL cached posts + current page posts
  const displayPosts: InsightPost[] = isSearching
    ? (() => {
        const q = searchQuery.toLowerCase().trim();
        // Search postCache (spans all visited pages)
        const fromCache = Array.from(postCache.values()).filter((p) =>
          p.title.toLowerCase().includes(q),
        );
        // Search current page posts (may have items not yet in postCache)
        const fromPage = posts.filter(
          (p) => !postCache.has(p.id) && p.title.toLowerCase().includes(q),
        );
        // Merge, deduplicate by id
        const seen = new Set<number>();
        return [...fromCache, ...fromPage].filter((p) => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
      })()
    : posts;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.headerWrapper}>
          <div className={styles.inner}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className={styles.motionBox}
            >
              <h1 className={styles.title}>TICCA Insights</h1>

              <p className={styles.subtitle}>
                Latest developments in AI technology and cognitive agents
              </p>

              {/* Stats */}
              {/* <div className={styles.stats}>
                <div className={styles.statItem}>
                  <TrendingUp className={styles.icon} />
                  <span>{formatNumber(totalViews)} Views</span>
                </div>

                <div className={styles.divider} />

                <div className={styles.statItem}>
                  <span>{totalPosts} Articles</span>
                </div>
              </div> */}

              {/* Search */}
              <div className={styles.searchWrapper}>
                <div className={styles.search}>
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={styles.searchInput}
                  />
                  <Search className={styles.searchIcon} />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className={styles.searchClearBtn}
                      title="Clear search"
                      aria-label="Clear search"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Category Pills */}
              <div className={styles.categories}>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`${styles.categoryBtn} ${
                      selectedCategory === category ? styles.active : ""
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
        <div className="min-h-screen bg-gray-50">
          <div className="mx-auto px-3 py-8">
            <div ref={gridRef} className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.max(1, Math.floor(containerSize.w / 220))}, minmax(0, 1fr))` }}>
              {loading ? (
                Array.from({ length: perPage }).map((_, i) => (
                  <CardSkeleton key={i} />
                ))
              ) : displayPosts.length ? (
                displayPosts?.map((post: InsightPost, index: number) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    onClick={() =>
                      navigator(`/insights/${post.slug}`, {
                        state: {
                          fromInsights: `${location.pathname}${location.search}`,
                          fromInsightsPage: page,
                          selectedCategory,
                        },
                      })
                    }
                    className="h-full cursor-pointer"
                  >
                    <PostCard
                      post={post}
                      showBookmark={true}
                      showCategory={true}
                      showDescription={true}
                      onBookmarkChange={refreshBookmarks}
                    />
                  </motion.article>
                ))
              ) : hasResolvedFirstFetch ? (
                <div className="col-span-full flex items-center justify-center py-16">
                  <div className="max-w-xl w-full text-center bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                    {selectedCategory === "Bookmarked" ? (
                      <>
                        <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                          No bookmarked posts yet
                        </h2>
                        <p className="text-gray-500">
                          Click the <Bookmark className="w-4 h-4 inline text-gray-400" /> icon on any post to bookmark it.
                        </p>
                      </>
                    ) : isSearching && searching ? (
                      <SearchLoader />
                    ) : isSearching ? (
                      <div className="flex flex-col items-center py-6">
                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                          <Search className="h-7 w-7 text-gray-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          No posts match your search
                        </h2>
                        <p className="mt-2 max-w-md text-sm text-gray-500">
                          We couldn't find anything for "<span className="font-medium text-gray-700">{searchQuery.trim()}</span>". Try a different keyword or browse by category.
                        </p>
                      </div>
                    ) : (
                      <h2 className="text-2xl font-semibold text-gray-900">
                        No articles available right now
                      </h2>
                    )}
                  </div>
                </div>
              ) : (
                Array.from({ length: perPage }).map((_, i) => (
                  <CardSkeleton key={i} />
                ))
              )}
            </div>
            {!isSearching && filteredTotalPages > 1 && 
            <div className="flex flex-row items-center justify-center gap-6 flex-wrap mt-12">
              {/* Stats */}
              <div className="text-sm text-gray-500 whitespace-nowrap">
                Page {page} of {filteredTotalPages} · <span className="font-medium text-gray-700">{visitedPages.size}</span> visited
              </div>

              {/* Pagination controls */}
              <div className="flex items-center gap-1.5">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 transition-colors"
                  aria-label="Previous page"
                >
                  ◀
                </button>

                {(() => {
                  const maxVisible = 5;
                  const half = Math.floor(maxVisible / 2);
                  let start = Math.max(1, page - half);
                  let end = Math.min(filteredTotalPages, start + maxVisible - 1);
                  if (end - start + 1 < maxVisible) {
                    start = Math.max(1, end - maxVisible + 1);
                  }
                  const pages: number[] = [];
                  for (let i = start; i <= end; i++) pages.push(i);
                  return pages.map((p) => {
                    const isCurrent = p === page;
                    const isVisited = visitedPages.has(p);
                    let btnClass = "flex items-center justify-center w-9 h-9 rounded-lg border text-sm font-medium transition-colors ";
                    if (isCurrent) {
                      btnClass += "bg-blue-600 border-blue-600 text-white shadow-sm";
                    } else if (isVisited) {
                      btnClass += "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100";
                    } else {
                      btnClass += "bg-white border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300";
                    }
                    return (
                      <button
                        key={p}
                        onClick={() => { markPageVisited(p); setPage(p); }}
                        className={btnClass}
                      >
                        {p}
                      </button>
                    );
                  });
                })()}

                <button
                  disabled={page === filteredTotalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 transition-colors"
                  aria-label="Next page"
                >
                  ▶
                </button>
              </div>

              {/* Go to page input */}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="text-gray-500 font-medium">Go to:</span>
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <input
                    type="number"
                    min={1}
                    max={filteredTotalPages}
                    value={goToInput}
                    onChange={(e) => setGoToInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleGoToPage(); }}
                    placeholder={`1–${filteredTotalPages}`}
                    className="pagination-go-input w-16 pr-5 pl-2 py-1.5 text-center text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 border-0 rounded-l-lg"
                  />
                  <div className="flex flex-col border-l border-gray-200 rounded-r-lg overflow-hidden">
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => {
                        const p = Math.min(page + 1, filteredTotalPages);
                        markPageVisited(p);
                        setPage(p);
                      }}
                      disabled={page >= filteredTotalPages}
                      className="flex items-center justify-center w-4 h-3.5 text-[8px] leading-none bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed border-b border-gray-200"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => {
                        const p = Math.max(page - 1, 1);
                        markPageVisited(p);
                        setPage(p);
                      }}
                      disabled={page <= 1}
                      className="flex items-center justify-center w-4 h-3.5 text-[8px] leading-none bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ▼
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleGoToPage}
                  disabled={!goToInput.trim()}
                  className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  Go
                </button>
              </div>
            </div> }
          </div>
        </div>
      </div>
    </>
  );
};
export default Insights;
